<template>
  <div>
    <b-row class="align-items-center">
      <b-col class="col-md-2">
        <AppSearchInput
          :label="$t('general.search') + ':'"
          :placeholder="$t('general.search')"
          @callback="fetchUserList"
        />
      </b-col>
      <b-col class="col-md-2">
        <b-form-group :label="$t('filter_role') + ':'">
          <v-select
            v-model="filterData.role"
            class="bg-white rounded"
            :get-option-label="
              (el) => {
                return el.name[$i18n.locale];
              }
            "
            :reduce="(p) => p.id"
            :options="GET_ROLE"
            @input="filterUser"
          />
        </b-form-group>
      </b-col>

      <b-col class="col-md-2 ml-auto text-right">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          v-b-modal.userManagementModalId
          v-b-modal.user-modal
          class="mt-1"
          variant="primary"
        >
          <feather-icon icon="PlusIcon" size="16" />
          {{ $t("add") }}
        </b-button>
      </b-col>
    </b-row>

    <b-table
      v-if="!isLoading"
      id="user-table"
      class="px-1 rounded mt-1 bg-light-secondary dark"
      :fields="fields"
      :items="GET_USERS.results"
      :tbody-tr-class="['tableRowClass']"
      show-empty
      small
      responsive
      :empty-text="$t('empty_text')"
      @row-clicked="onRowSelected"
    >
      <template #head()="props">
        <span>{{ $t(props.label) }}</span>
      </template>

      <template #cell(fio)="props">
        <h6 class="font-weight-bolder mb-0">
          {{ props.item && props.item.first_name ? props.item.first_name : '' }}
        </h6>
        <small>{{ props.item && props.item.last_name ? props.item.last_name : '' }}</small>
      </template>

      <template #cell(last_login)="props">
        <div v-if="!props.item.last_login">
          {{ $t("not_yet_registered_with_this_login") }}
        </div>

        <div v-else>
          <h6 class="font-weight-bolder">
            {{ $dayjs(props.value).format("DD.MM.YYYY") }},
            {{ $dayjs(props.value).format("HH:mm") }}
          </h6>
        </div>
      </template>

      <template #cell(photo)="{ value }">
        <b-avatar :src="value" size="40" variant="light-primary" />
      </template>

      <template #cell(is_active)="props">
        <b-form-checkbox
          :checked="props.item.is_active"
          class="custom-control-primary mr-0"
          name="check-button"
          switch
          disabled
        >
          <span class="switch-icon-left">
            <feather-icon icon="CheckIcon" />
          </span>
          <span class="switch-icon-right">
            <feather-icon icon="XIcon" />
          </span>
        </b-form-checkbox>
      </template>

      <template #cell(role)="props">
        <div v-if="props.item.role">
          <b-badge
            v-for="(item, index) in props.item.role_name"
            :key="index"
            variant="primary"
            class="mr-50"
          >
            {{ item }}
          </b-badge>
        </div>
      </template>

      <template #cell(actions)="props">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          size="sm"
          class="mt-50 mb-50"
          variant="warning"
          @click="$router.push(`/users/edit/${props.item.id}`)"
        >
          <feather-icon icon="EditIcon" size="15" />
        </b-button>

        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          size="sm"
          variant="danger"
          class="ml-1 mt-50 mb-50"
          @click="deleteUser(props)"
        >
          <feather-icon icon="TrashIcon" size="15" />
        </b-button>
      </template>
    </b-table>
    <!-- </b-card> -->

    <b-card v-else>
      <Loader />
    </b-card>

    <b-card>
      <div class="d-flex justify-content-between">
        <div class="d-flex align-items-center mb-0">
          <span class="text-nowrap ml-1">
            {{ $t("pagination.show_from") }}
          </span>
          <b-form-select
            v-model="filterData.page_size"
            :options="['10', '15', '20']"
            class="mx-1"
            @change="changePerPage"
          />
          <span class="text-nowrap"> {{ $t("pagination.lines") }} </span>
        </div>

        <b-pagination
          v-model="filterData.page"
          :per-page="filterData.page_size"
          :total-rows="GET_USERS.count"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          @input="fetchUserListPagination"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-card>

    <UserModal />
  </div>
</template>

<script>
import Ripple from "vue-ripple-directive";
import loader from "@/mixins/loader";
import vSelect from "vue-select";
import { mapGetters, mapActions } from "vuex";
import {
  BButton,
  BFormGroup,
  BRow,
  BCol,
  BTable,
  BAvatar,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BPagination,
  BCard,
  BBadge,
} from "bootstrap-vue";
import areYouSure from "@/mixins/areYouSure";
import UserModal from "@/views/modules/Administrator/userManagement/components/UserModal.vue";
import AppSearchInput from "@/components/AppSearchInput.vue";
import Loader from "@/components/Loader.vue";

export default {
  components: {
    BButton,
    vSelect,
    BFormGroup,
    BRow,
    BCol,
    BTable,
    BAvatar,
    BFormCheckbox,
    UserModal,
    BFormInput,
    BFormSelect,
    BPagination,
    BCard,
    BBadge,
    AppSearchInput,
    Loader,
  },
  directives: {
    Ripple,
  },
  mixins: [areYouSure, loader],
  data() {
    return {
      isLoading: false,
      filterData: {
        page: 1,
        page_size: 10,
        search: "",
        role: null,
      },
      initTimeout: null,
      fields: [
        {
          label: "№",
          key: "id",
          thClass: "tableHeadClass",
        },
        {
          label: "photo",
          key: "photo",
          thClass: "tableHeadClass",
        },
        {
          label: "fio",
          key: "fio",
          thClass: "tableHeadClass",
        },
        {
          label: "login",
          key: "username",
          thClass: "tableHeadClass",
        },
        {
          label: "role",
          key: "role",
          thClass: "tableHeadClass",
        },
        {
          label: "last_active",
          key: "last_login",
          thClass: "tableHeadClass",
        },
        {
          label: "status",
          key: "is_active",
          thClass: "tableHeadClass",
        },
        {
          label: "actions",
          key: "actions",
          thClass: "tableHeadClass",
        },
      ],
      test: null,
    };
  },
  computed: {
    ...mapGetters("users", ["GET_USERS"]),
    ...mapGetters("role", ["GET_ROLE"]),
  },

  async mounted() {
    // await this.FETCH_ROLE();
    this.isLoading = true;
    // await this.$_loaderStart();
    await this.FETCH_USERS(this.filterData);
    this.isLoading = false;
  },
  // async beforeRouteLeave(to, from, next) {
  //   await this.$_loaderStop();
  //   next();
  // },

  methods: {
    ...mapActions("users", ["FETCH_USERS", "CREATE_USER", "DELETE_USER"]),
    ...mapActions("role", ["FETCH_ROLE"]),
    fetchUserListPagination() {
      clearTimeout(this.initTimeout);
      this.initTimeout = setTimeout(async () => {
        this.isLoading = true;
        let response = await this.FETCH_USERS(this.filterData);

        if (
          response &&
          response.response &&
          response.response.data.detail == "Invalid page."
        ) {
          this.filterData.page = 1;
          await this.FETCH_USERS(this.filterData);
        }
        this.isLoading = false;
      }, 400);
    },
    fetchUserList(value) {
      this.filterData.search = value;
      this.fetchUserListPagination();
    },

    async filterUser() {
      this.isLoading = true;
      await this.FETCH_USERS(this.filterData);
      this.isLoading = false;
    },

    async changePerPage() {
      this.isLoading = true;

      await this.FETCH_USERS(this.filterData);
      this.isLoading = false;
    },

    deleteUser(props) {
      this.$_showAreYouSureModal().then((val) => {
        if (val) {
          this.DELETE_USER(props.item.id)
            .then(() => {
              this.FETCH_USERS(this.filterData);
              this.$_okToast();
            })
            .catch((err) => {
              this.$_errToast(err);
            });
        }
      });
    },

    onRowSelected(params) {
      this.$router.push(`/users/edit/${params.id}`);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
@import "@core/scss/vue/libs/vue-select.scss";

#user-table {
  border-collapse: separate;
  border-spacing: 0 1rem;
}

.dark-layout #user-table .tableRowClass {
  background: #19213a;
}

.dark-layout .dark {
  background-color: #283046 !important;
}

#user-table .tableRowClass {
  background: white;
  transform: translateY(5px);
  transition: all 0.5s ease-in-out;
  text-align: center;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);

  td {
    border-top: 0;
    padding: 20px !important;

    &:first-child {
      border-top-left-radius: 0.5rem;
      border-bottom-left-radius: 0.5rem;
    }

    &:last-child {
      border-top-right-radius: 0.5rem;
      border-bottom-right-radius: 0.5rem;
    }
  }
}

#user-table .tableRowClass:hover {
  transform: translateY(0);
}

#user-table .tableHeadClass {
  background: none !important;
  border: none !important;
}

#user-table .tableRowClass:hover {
  .tableTitle {
    color: #7367f0 !important;
  }
}

.tableHeadClass {
  text-align: center;
  padding-left: 0 !important;
}

.tableRowClass {
  cursor: pointer;
}
</style>
